import { Button, Dropdown, Menu, Drawer } from "antd";
import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, SwitchButton } from "components/base";
import { useHistory, useLocation } from "react-router-dom";
import { setBatterySearch, setModalName, setVehiclesSearch } from "store/actions";
import {
  actionMenu,
  exportIcon,
  plusIcon,
  searchIcon,
  zonesSmallIcon,
  addAssigneeIcon,
  archiveIcon,
  uploadIconSmall,
} from "svgs";
import { ROUTES } from "utils/constants";
import { AppState } from "store";
import {
  MenuOutlined,
  SearchOutlined,
  PlusOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import "styles/components/tab-headers/battery-header.scss";

export default function BatteryHeader() {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  // Mobile state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth < 992
  );

  const selectedView = location.pathname.split("/")[2];
  const { showbulkActionsVehicles } = useSelector<AppState, AppState["vehicles"]>(
    (state) => state.vehicles
  );
  const {
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  // Handle responsive breakpoints
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onBulkVehiclesFirmwareUpdate = useCallback(() => {
    dispatch(setModalName("bulk_update_vehicle_firmware"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onAddBattery = useCallback(() => {
    dispatch(setModalName("add_battery"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onSwitchView = useCallback(
    (event) => {
      const { value } = event.target;
      if (value == "Battery Models") history.push(`${ROUTES.battery_model}/table`);
      setMobileMenuOpen(false);
    },
    [history]
  );

  const onImportCsv = useCallback(() => {
    dispatch(setModalName("import_battery_csv"));
    setMobileMenuOpen(false);
  }, [dispatch]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setBatterySearch(event.target.value));
    },
    [dispatch]
  );

  const menu = (
    <Menu className="vehicle-drop-menu">
      <Menu.Item key="0" onClick={onImportCsv}>
        <span>Import CSV</span>
      </Menu.Item>
    </Menu>
  );

  // Mobile menu content
  const mobileMenuContent = (
    <div className="mobile-menu-content">
      {/* Content Type Switch */}
      <div className="mobile-menu-section">
        <h4 className="section-title">Content Type</h4>
        <SwitchButton
          label=""
          options={["Batteries", "Battery Models"]}
          defaultOption={"Batteries"}
          onChange={onSwitchView}
          type="secondary"
        />
      </div>

      {/* Actions */}
      {(userType == 1 || userType == 2) && (
        <div className="mobile-menu-section">
          <h4 className="section-title">Actions</h4>
          <div className="mobile-action-buttons">
            <Button type="default" onClick={onImportCsv} block>
              Import CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddBattery}
              block
            >
              Add Battery
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {showbulkActionsVehicles && (
        <div className="mobile-menu-section">
          <h4 className="section-title">Bulk Actions</h4>
          <div className="mobile-action-buttons">
            <Button danger icon={<DeleteOutlined />} disabled block>
              Archive
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="battery-header common-header">
      {/* Desktop & Tablet View */}
      <div className="header-desktop">
        {/* Search Input */}
        <div className="search-wrapper">
          <Input
            customClasses="--standard"
            placeholder="Search Batteries..."
            prefix={searchIcon}
            onChange={onChange}
          />
        </div>

        {/* Bulk Actions */}
        {showbulkActionsVehicles && (
          <article className="bulk-actions">
            <Button disabled danger icon={archiveIcon}>
              <span className="btn-text">Archive</span>
            </Button>
          </article>
        )}

        {/* Actions Container */}
        <div className="actions-container">
          <SwitchButton
            label=""
            options={["Batteries", "Battery Models"]}
            defaultOption={"Batteries"}
            onChange={onSwitchView}
            type="secondary"
          />

          <div className="vertical-separator" />

          {(userType == 1 || userType == 2) && (
            <>
              <Dropdown overlay={menu} trigger={["click"]} className="vehicle-menu">
                <a
                  className="ant-dropdown-link"
                  onClick={(e) => e.preventDefault()}
                >
                  {actionMenu}
                </a>
              </Dropdown>
              <Button
                type="primary"
                icon={<span className="custom-icon">{plusIcon}</span>}
                onClick={onAddBattery}
              >
                <span className="btn-text">Add Battery</span>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Mobile View */}
      <div className="header-mobile">
        {/* Top Row */}
        <div className="mobile-top-row">
          {/* Search Toggle or Input */}
          {mobileSearchOpen ? (
            <div className="mobile-search-expanded">
              <Input
                customClasses="--standard mobile-search-input"
                placeholder="Search Batteries..."
                prefix={searchIcon}
                onChange={onChange}
                autoFocus
              />
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => setMobileSearchOpen(false)}
                className="search-close-btn"
              />
            </div>
          ) : (
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={() => setMobileSearchOpen(true)}
              className="mobile-search-btn"
            />
          )}

          {/* Actions Wrapper - Right Side */}
          {!mobileSearchOpen && (
            <div className="mobile-actions-wrapper">
              {/* Primary Action Button */}
              {(userType == 1 || userType == 2) && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onAddBattery}
                  className="mobile-add-btn"
                >
                  <span className="mobile-add-text">Add</span>
                </Button>
              )}

              {/* Menu Button */}
              <Button
                type="text"
                icon={actionMenu}
                onClick={() => setMobileMenuOpen(true)}
                className="mobile-menu-btn"
              />
            </div>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {showbulkActionsVehicles && (
          <div className="mobile-bulk-actions">
            <Button size="small" danger icon={<DeleteOutlined />} disabled>
              Archive
            </Button>
          </div>
        )}
        <h1 style={{ paddingTop: "6px" }}>Batteries</h1>
      </div>

      {/* Mobile Drawer Menu */}
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        visible={mobileMenuOpen}
        className="battery-mobile-drawer"
        width={280}
      >
        {mobileMenuContent}
      </Drawer>
    </div>
  );
}